import styled from 'styled-components'
import { Box } from '../../atoms/Box'

// Re-export shared styles from SectionAnalysisStyles
export {
  SectionAnalysisTabsContainer as TabsContainer,
  SectionAnalysisTabButton as TabButton,
  SectionAnalysisContentContainer as ContentContainer,
  SectionAnalysisLoadingContainer as LoadingContainer,
  SectionAnalysisErrorContainer as ErrorContainer,
  SectionAnalysisTabContentContainer as TabContentContainer,
  SectionAnalysisMetricsGrid as MetricsGrid,
  SectionAnalysisChartGrid as ChartGrid,
  SectionAnalysisChartContainer as ChartContainer,
  SectionAnalysisChartTitle as ChartTitle,
  SectionAnalysisChartSubtitle as ChartSubtitle,
  SectionAnalysisInsightCard as InsightCard,
  SectionAnalysisInsightTitle as InsightTitle,
  SectionAnalysisInsightText as InsightText,
  SectionAnalysisFilterSection as FilterSection,
  SectionAnalysisFilterBar as FilterBar,
  SectionAnalysisLocationFilters as LocationFilters,
  SectionAnalysisFilterChip as FilterChip,
  SectionAnalysisLocationBadge as LocationBadge,
  SectionAnalysisDivider as Divider,
} from '../../molecules/SectionAnalysisStyles/SectionAnalysisStyles'

// Tax-specific container
export const TaxContainer = styled(Box)`
  width: 100%;
  background: ${({ theme }) => theme.colors.background.primary};
  padding-bottom: 48px;
`

// Year selector container
export const YearSelectorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 8px;
  }
`

export const YearLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const YearSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 12px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

// Profile filter chips container
export const ProfileFiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 20px;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 8px;
  }
`

export const ProfileFilterChip = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.background.secondary};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.text.inverse : theme.colors.text.primary};
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primary : theme.colors.border.light};
  border-radius: 16px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${({ theme, $active }) =>
      $active ? theme.colors.primaryHover : theme.colors.background.tertiary};
    border-color: ${({ theme, $active }) =>
      $active ? theme.colors.primaryHover : theme.colors.border.default};
  }

  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 14px;
  }
`

// Comparison card styles
export const ComparisonCard = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  margin-bottom: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`

export const ComparisonCardTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px 0;
`

// Geographic comparison — profile cards grid
export const ComparisonCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

export const ComparisonProfileCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: 16px;
`

export const ComparisonProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 3px;
`

export const ComparisonProfileTitle = styled.h4`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`

export const ComparisonProfileValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 12px;
`

export const ComparisonBarsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`

export const ComparisonBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

export const ComparisonBarLabel = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.secondary};
  width: 96px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100px;
  }
`

export const ComparisonBarTrack = styled.div`
  flex: 1;
  height: 6px;
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: 3px;
  overflow: hidden;
`

export const ComparisonBar = styled.div<{
  $width: number
  $color: 'neutral' | 'success' | 'danger'
}>`
  height: 100%;
  width: ${({ $width }) => Math.min($width, 100)}%;
  background: ${({ theme, $color }) => {
    switch ($color) {
      case 'success':
        return theme.colors.success
      case 'danger':
        return theme.colors.danger
      default:
        return theme.colors.text.secondary
    }
  }};
  border-radius: 3px;
  transition: width 0.3s ease;
`

export const ComparisonBarValue = styled.span<{
  $color: 'neutral' | 'success' | 'danger'
}>`
  font-size: 11px;
  font-weight: 600;
  min-width: 38px;
  text-align: right;
  color: ${({ theme, $color }) => {
    switch ($color) {
      case 'success':
        return theme.colors.success
      case 'danger':
        return theme.colors.danger
      default:
        return theme.colors.text.primary
    }
  }};
`

export const ComparisonDiffText = styled.span<{ $positive: boolean }>`
  display: block;
  font-size: 11px;
  color: ${({ theme, $positive }) =>
    $positive ? theme.colors.success : theme.colors.danger};
  margin-left: 102px;
  margin-top: -5px;
  margin-bottom: 2px;

  @media (max-width: 768px) {
    margin-left: 108px;
  }
`

// Profile details table
export const ProfileTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
`

export const TableHeaderWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

export const ProfileTableHeader = styled.th`
  padding: 10px 12px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};

  &:first-child {
    border-top-left-radius: ${({ theme }) => theme.borderRadius.medium};
  }

  &:last-child {
    border-top-right-radius: ${({ theme }) => theme.borderRadius.medium};
  }
`

export const ProfileTableRow = styled.tr`
  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &:last-child td {
    border-bottom: none;
  }
`

export const ProfileTableCell = styled.td`
  padding: 10px 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`

// Insight card styles
export const TaxInsightCard = styled.div<{
  $variant?: 'high' | 'medium' | 'low'
}>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  background: ${({ theme, $variant }) => {
    switch ($variant) {
      case 'high':
        return theme.colors.successLight
      case 'medium':
        return theme.colors.warningLight
      case 'low':
        return theme.colors.background.tertiary
      default:
        return theme.colors.background.tertiary
    }
  }};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border-left: 4px solid
    ${({ theme, $variant }) => {
      switch ($variant) {
        case 'high':
          return theme.colors.success
        case 'medium':
          return theme.colors.warning
        case 'low':
          return theme.colors.danger
        default:
          return theme.colors.primary
      }
    }};
`

export const TaxInsightIcon = styled.span`
  font-size: 16px;
  flex-shrink: 0;
`

export const TaxInsightContent = styled.div`
  flex: 1;
`

export const TaxInsightTitle = styled.h4`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 3px 0;
`

export const TaxInsightText = styled.p`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
  line-height: 1.4;
`

// Tax rate card styles
export const RateBarContainer = styled.div`
  margin: 16px 0 12px;
`

export const RateBarTrack = styled.div`
  display: flex;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background.tertiary};
`

export const RateBarSegment = styled.div<{
  $width: number
  $color: string
  $position: 'left' | 'right'
}>`
  width: ${({ $width }) => $width}%;
  background: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  ${({ $position }) =>
    $position === 'left'
      ? 'border-radius: 4px 0 0 4px;'
      : 'border-radius: 0 4px 4px 0;'}
`

export const RateBarLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 8px;
`

export const RateLegend = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
`

export const RateLegendItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const RateLegendDot = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

export const RateDetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
`

export const RateDetailLabel = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  width: 100px;
  flex-shrink: 0;
`

export const RateDetailValue = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  width: 70px;
  flex-shrink: 0;
`

export const RateDetailBarTrack = styled.div`
  flex: 1;
  height: 8px;
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: 4px;
  overflow: hidden;
`

export const RateDetailBar = styled.div<{ $width: number; $color: string }>`
  height: 100%;
  width: ${({ $width }) => $width}%;
  background: ${({ $color }) => $color};
  opacity: 0.7;
  border-radius: 4px;
  transition: width 0.3s ease;
`

export const RateChurchNote = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-style: italic;
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
`

// Calculator link banner
export const CalculatorLinkBanner = styled.a`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  padding: 16px 20px;
  background: ${({ theme }) => theme.colors.primaryLight};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  text-decoration: none;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    opacity: 0.85;
  }

  @media (max-width: 768px) {
    padding: 12px 16px;
    gap: 12px;
  }
`

export const CalculatorLinkContent = styled.div`
  flex: 1;
`

export const CalculatorLinkTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 3px;
`

export const CalculatorLinkDescription = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
`
